import { createSelector } from '@reduxjs/toolkit';
import { readFromObj } from 'utils/utils';
import { FENIX_EXPORTED_TO_YMS_FIELD } from 'utils/fenixYms';
import {
    RELATION_TYPE_DROPDOWN_OPTION,
    INDUSTRY_TYPE_DROPDOWN_OPTION,
    PROPERTY_NOT_FOUND,
    PROCESS_TIME_FIELD,
    DRIVER_NAME_FIELD,
} from 'utils/constants';
import { hasBlockingValidations } from 'utils/lookupBlocking';
import { normalizeRelayEnforcement, showsRelayWarning, blocksRelaySubmit } from 'utils/relayEnforcement';
import { resolveFaceMatchConfig, resolveFaceMatchStatus } from 'utils/faceMatchEnforcement';
import { selectFaceMatchLatestAttempt } from 'store/faceMatchSlice/faceMatchSelectors';

const AGENT_TIME_FIELD = 'agentTime';

const emptyArray = [];
const emptyObj = {};

export const selectFormsSlice = (state) => state.forms;

export const selectFormsObject = createSelector([selectFormsSlice], (formsSlice) => formsSlice.forms);
export const selectSubmitFormInProgressByFormId = (id) =>
    createSelector([selectFormsSlice, (_, formId = id) => formId], (formsSlice, formId) => !!formsSlice.submitFormInProgress?.[formId]);

export const selectDiscardTransactionInProgressByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.discardTransactionInProgress?.[transactionId]
    );
export const selectDiscardReasonDialogOpenByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.discardReasonDialogOpenByTransactionId?.[transactionId]
    );
export const selectDiscardReasonStartNewByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.discardReasonStartNewByTransactionId?.[transactionId]
    );
export const selectStartNewInProgressByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.startNewInProgress?.[transactionId]
    );
export const selectIsTransactionInProcessingStateById = (id) =>
    createSelector(
        [selectSubmitFormInProgressByFormId(id), selectDiscardTransactionInProgressByTransactionId(id), selectStartNewInProgressByTransactionId(id)],
        (submittingTransactionId, discardingTransactionId, startNewInProgress) =>
            submittingTransactionId || discardingTransactionId || startNewInProgress
    );
export const selectDraftMailTransactionInProgressByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.draftMailTransactionInProgress?.[transactionId]
    );
export const selectDraftMailDialogOpenByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.draftMailDialogOpenByTransactionId?.[transactionId]
    );

export const selectReturnToQueueInProgress = createSelector([selectFormsSlice], (formsSlice) => formsSlice.returnToQueueInProgress);
export const selectReturnToQueueError = createSelector([selectFormsSlice], (formsSlice) => formsSlice.returnToQueueError);

export const selectTruckTransactionFormConfig = createSelector([selectFormsSlice], (formsSlice) => formsSlice.formsConfig.truckTransactionForm);

export const selectFormById = (id) => createSelector([selectFormsObject, (_, formId = id) => formId], (forms, id) => forms[id]);

export const selectTransactionDataByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, tranasctionId = id) => tranasctionId], (forms, id) => forms[id]?.transactionData);

export const selectRelaysConfigByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, tranasctionId = id) => tranasctionId], (forms, id) => forms[id]?.relaysConfig || emptyArray);

export const selectRelayUsedByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, tranasctionId = id) => tranasctionId], (forms, id) => !!forms[id]?.relayUsed);

// A relay is Pulse-capable if it has at least one active signal whose operation is 'PULSE'
// (the same "active PULSE signal" rule used to sort relays in relayUtils.getOrderedActiveSignals).
export const getActivePulseRelaySlotIds = (relaysConfig = []) =>
    relaysConfig
        .filter((relay) => relay.signalLabels?.some((signal) => signal.active && signal.operation === 'PULSE'))
        .map((relay) => relay.relaySlotId);

// 'NONE' | 'SUBMIT' | 'ALL' - which quick actions this lane auto-pulses its Pulse relay(s) on.
export const selectAutomaticPulseByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, tranasctionId = id) => tranasctionId], (forms, id) => forms[id]?.automaticPulse || 'NONE');

export const selectPulseRelaySlotIdsByTransactionId = (id) =>
    createSelector([selectRelaysConfigByTransactionId(id)], (relaysConfig) => getActivePulseRelaySlotIds(relaysConfig));

const AUTOMATIC_PULSE_SUBMIT_ENABLED_VALUES = ['SUBMIT', 'ALL'];
const AUTOMATIC_PULSE_DISCARD_ENABLED_VALUES = ['ALL'];

// "Enabled" selectors ignore relay availability - they answer "would this action auto-pulse if a
// Pulse relay were configured". Used to distinguish "lane doesn't support this action at all"
// (silent no-op) from "lane supports it but no Pulse relay is configured" (UC4 warning).
export const selectAutomaticPulseSubmitEnabledByTransactionId = (id) =>
    createSelector([selectAutomaticPulseByTransactionId(id)], (automaticPulse) => AUTOMATIC_PULSE_SUBMIT_ENABLED_VALUES.includes(automaticPulse));

export const selectAutomaticPulseDiscardEnabledByTransactionId = (id) =>
    createSelector([selectAutomaticPulseByTransactionId(id)], (automaticPulse) => AUTOMATIC_PULSE_DISCARD_ENABLED_VALUES.includes(automaticPulse));

// Gate for the Quick Submit + Pulse shortcut: the lane must allow auto-pulse on submit AND have at
// least one Pulse-capable relay configured.
export const selectAutomaticPulseSubmitEligibleByTransactionId = (id) =>
    createSelector(
        [selectAutomaticPulseSubmitEnabledByTransactionId(id), selectPulseRelaySlotIdsByTransactionId(id)],
        (submitEnabled, pulseRelaySlotIds) => submitEnabled && pulseRelaySlotIds.length > 0
    );

// Gate for the Quick Discard + Pulse shortcut: the lane must allow auto-pulse on discard AND have
// at least one Pulse-capable relay configured.
export const selectAutomaticPulseDiscardEligibleByTransactionId = (id) =>
    createSelector(
        [selectAutomaticPulseDiscardEnabledByTransactionId(id), selectPulseRelaySlotIdsByTransactionId(id)],
        (discardEnabled, pulseRelaySlotIds) => discardEnabled && pulseRelaySlotIds.length > 0
    );

export const selectDiscardReasonOpenedViaPulseShortcutByTransactionId = (id) =>
    createSelector(
        [selectFormsSlice, (_, transactionId = id) => transactionId],
        (formsSlice, transactionId) => !!formsSlice.discardReasonOpenedViaPulseShortcutByTransactionId?.[transactionId]
    );

export const selectRelayEnforcementByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, transactionId = id) => transactionId], (forms, id) =>
        normalizeRelayEnforcement(forms[id]?.relayEnforcement)
    );

export const selectEffectiveRelaysConfigByTransactionId = (id) =>
    createSelector([selectRelaysConfigByTransactionId(id), selectTransactionDataByTransactionId(id)], (relaysConfig, transactionData) =>
        relaysConfig.length ? relaysConfig : transactionData?.camsConfig?.relays || transactionData?.camsConfig?.relayConfig || emptyArray
    );

export const selectRelayWarningRequiredByTransactionId = (id) =>
    createSelector(
        [selectEffectiveRelaysConfigByTransactionId(id), selectRelayUsedByTransactionId(id), selectRelayEnforcementByTransactionId(id)],
        (effectiveRelaysConfig, relayUsed, relayEnforcement) => showsRelayWarning(relayEnforcement) && effectiveRelaysConfig.length > 0 && !relayUsed
    );

export const selectLookupBlockingValidationsByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, transactionId = id) => transactionId], (forms, id) => forms[id]?.blockingValidations || emptyObj);

export const selectLookupSubmitBlockedByTransactionId = (id) =>
    createSelector([selectLookupBlockingValidationsByTransactionId(id)], (blockingValidations) => hasBlockingValidations(blockingValidations));

export const selectRelaySubmitBlockedByTransactionId = (id) =>
    createSelector(
        [selectEffectiveRelaysConfigByTransactionId(id), selectRelayUsedByTransactionId(id), selectRelayEnforcementByTransactionId(id)],
        (effectiveRelaysConfig, relayUsed, relayEnforcement) => blocksRelaySubmit(relayEnforcement) && effectiveRelaysConfig.length > 0 && !relayUsed
    );

export const selectFaceMatchConfigByTransactionId = (id) =>
    createSelector([selectTransactionDataByTransactionId(id)], (transactionData) => resolveFaceMatchConfig(transactionData?.gate));

export const selectFaceMatchSubmitStatusByTransactionId = (id) =>
    createSelector([selectFaceMatchConfigByTransactionId(id), selectFaceMatchLatestAttempt(id)], resolveFaceMatchStatus);

export const selectSubmitBlockDialogByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, transactionId = id) => transactionId], (forms, id) => forms[id]?.submitBlockDialog || null);

export const selectSubmitBlockDialogOpenByTransactionId = (id) => createSelector([selectSubmitBlockDialogByTransactionId(id)], (dialog) => !!dialog);

export const selectFormConfigByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, tranasctionId = id) => tranasctionId], (forms, id) => forms[id]?.formConfig);

export const selectActiveSelectionTableNameByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, transactionId = id) => transactionId], (forms, id) => forms[id]?.activeSelectionTableName);

export const selectDroppedCopyByTransactionId = (id) =>
    createSelector([selectFormsObject, (_, transactionId = id) => transactionId], (forms, id) => forms[id]?.droppedCopy);

export const selectTransactionFormStateByTransactionId = (transactionId) =>
    createSelector([selectFormsObject, (_, id = transactionId) => id], (forms, id) => forms[id]?.form || emptyObj);

export const createFormStateByTransactionIdSelector = (id) =>
    createSelector([selectTransactionFormStateByTransactionId(id)], (formState) => formState);

export const createExternalAnnouncementsSelector = (formId, announcementSource) =>
    createSelector(
        [selectFormById(formId), (_, source = announcementSource) => source],
        (form, announcementSource) => form?.externalAnnouncements?.[announcementSource] || emptyArray
    );

export const createTransactionEncodedImagesByTransactionIdSelector = (id, aiOcrImagesOnly = false) =>
    createSelector([selectTransactionFormStateByTransactionId(id)], (formState) => {
        let encodedImages = emptyArray;
        if (formState?.encodedImages) encodedImages = formState?.encodedImages;
        if (!aiOcrImagesOnly && formState?.cameraSnapshots) encodedImages = [...encodedImages, ...(formState?.cameraSnapshots || emptyArray)];
        return encodedImages;
    });

export const createFormFieldsByTransactionIdSelector = (id) => {
    return createSelector(
        [selectFormConfigByTransactionId(id), selectRelaysConfigByTransactionId(id), selectTransactionDataByTransactionId(id)],

        (formConfig, relaysConfig, transactionData) => {
            if (!formConfig) return { relaysConfig };

            let formFields = [];
            let automationFields = [];
            const { name, fields } = formConfig;

            fields.forEach((oldField) => {
                const field = structuredClone(oldField);
                const { lookup } = field;
                const initialValue = transactionData ? readFromObj(transactionData, field.dataField) : PROPERTY_NOT_FOUND;
                const referenceField = field.referenceField;
                if (referenceField) {
                    const initialReferenceValue = transactionData ? readFromObj(transactionData, field.referenceField) : PROPERTY_NOT_FOUND;
                    initialReferenceValue !== PROPERTY_NOT_FOUND && (field.initialReferenceValue = initialReferenceValue);
                }

                if (initialValue !== PROPERTY_NOT_FOUND) {
                    field.initialValue = initialValue;

                    if (field.dataField === FENIX_EXPORTED_TO_YMS_FIELD) {
                        const isUnknownOrEmpty = initialValue == null || initialValue === '' || initialValue.toString().toUpperCase() === 'UNKNOWN';
                        if (isUnknownOrEmpty) field.initialValue = '';
                    }

                    if (field.automationField) automationFields.push(field.dataField);
                    if (lookup && lookup.dataSource) {
                        const displayExpr = lookup.displayExpr;

                        if (
                            field.dataField === 'person.fullName' ||
                            (field.dataField === 'person.companyName' && Array.isArray(lookup?.dataSource))
                        ) {
                            const idField = field.lookup.referenceExpr;
                            field.lookup.dataSource = field.lookup.dataSource.reduce((newLookup, entry) => {
                                const registered = newLookup.find(({ [idField]: id }) => id === entry[idField]);

                                const alert = {
                                    color: entry.color,
                                    note: entry.note,
                                    name: entry.name,
                                    accessPeriod: {
                                        accessType: entry.accessType,
                                        periodType: entry.periodType,
                                        weekdays: entry.weekdays,
                                        startTime: entry.startTime,
                                        endTime: entry.endTime,
                                    },
                                };
                                if (registered) {
                                    if (!alert.color) registered.alerts.push(alert);
                                    else registered.alerts.unshift(alert);
                                } else {
                                    // eslint-disable-next-line no-unused-vars
                                    const { color, note, name, ...newEntry } = entry;
                                    newEntry.alerts = [alert];
                                    newEntry.relationType = RELATION_TYPE_DROPDOWN_OPTION[entry.relationTypeId]?.label || '';
                                    newEntry.industryType = INDUSTRY_TYPE_DROPDOWN_OPTION[entry.industryType]?.label || '';
                                    newLookup.push(newEntry);
                                }
                                return newLookup;
                            }, []);
                        }

                        if (!displayExpr) field.lookup.dataSource = lookup.dataSource.map(({ value }) => value ?? '').sort();
                        else if (
                            field.dataField !== 'vehicle.transportType' &&
                            field.dataField !== 'announcement.transportType' &&
                            // Process Time (BEAST-9186): keep the backend-defined option order
                            // (<30s, <1 min, <2 min, >2 min, /) instead of alphabetical sorting.
                            field.dataField !== PROCESS_TIME_FIELD
                        ) {
                            let duplicates = {};
                            field.lookup.dataSource.sort((first, second) => {
                                const b = second[displayExpr];
                                const a = first[displayExpr];
                                if (a === b) {
                                    duplicates[a] ?? (duplicates[a] = 0);
                                    !first.duplicate && (first.duplicate = ++duplicates[a]);
                                    !second.duplicate && (second.duplicate = ++duplicates[a]);
                                }
                                return a?.toLowerCase() < b?.toLowerCase() ? -1 : 1;
                            });
                        }
                    }

                    formFields.push(field);
                }
            });
            return { name, formFields, relaysConfig, automationFields };
        }
    );
};

export const createInventoryDataByLocationIdSelector = (locationId) =>
    createSelector([selectFormsSlice, (_, id = locationId) => id], (formsSlice, locationId) => formsSlice.inventoryData[locationId] || emptyArray);

export const selectInventoryMappingByFormId = (state, formId) => selectFormsSlice(state).inventoryMapping?.[formId];

export const createEquipmentDataByLocationIdSelector = (locationId) =>
    createSelector([selectFormsSlice, (_, id = locationId) => id], (formsSlice, locationId) => formsSlice.equipmentData[locationId] || emptyArray);

export const selectRecentValueData = createSelector([selectFormsSlice], (formsSlice) => formsSlice.recentValueData);

export const createAiSuggestionHistoryByTransactionIdSelector = (id) =>
    createSelector([selectFormsObject, (_, formId = id) => formId], (forms, formId) => forms[formId]?.form?.aiSuggestionHistory || emptyObj);

export const createSheetAnnouncementValuesByTransactionIdSelector = (id) =>
    createSelector([selectFormsObject, (_, formId = id) => formId], (forms, formId) => forms[formId]?.form?.sheetAnnouncementValues || emptyObj);
export const createFieldHelperTextsByTransactionIdSelector = (id) =>
    createSelector([selectFormsObject, (_, formId = id) => formId], (forms, formId) => forms[formId]?.form?.fieldHelperTexts || emptyObj);

export const createProcessTimeFieldSelector = (id) =>
    createSelector(
        [selectFormConfigByTransactionId(id)],
        (formConfig) => formConfig?.fields?.find((field) => field.dataField === PROCESS_TIME_FIELD) || null
    );

export const createProcessTimeContextSelector = (id) =>
    createSelector([selectTransactionFormStateByTransactionId(id), createProcessTimeFieldSelector(id)], (formState, processTimeField) => {
        if (!processTimeField) return null;
        return {
            hasField: true,
            agentTime: formState?.values?.[AGENT_TIME_FIELD] ?? null,
            driverValue: formState?.values?.[DRIVER_NAME_FIELD],
            driverSuggestionValue: formState?.automation?.[DRIVER_NAME_FIELD]?.sourceValue,
            isUserSelected: formState?.fieldValueSources?.[PROCESS_TIME_FIELD] === 'user' || !!formState?.userEdited?.[PROCESS_TIME_FIELD],
            currentSuggestionValue: formState?.automation?.[PROCESS_TIME_FIELD]?.sourceValue ?? null,
            currentFieldValue: formState?.values?.[PROCESS_TIME_FIELD] ?? null,
            fieldLookup: processTimeField.lookup ?? null,
        };
    });
